import type { Query, QueryClient } from '@tanstack/react-query';
import type { PrototypeDto } from '@aiai/shared';

/**
 * Matches every cached `qk.prototype(ref)` entry that holds this prototype.
 * Pages key the prototype by the route ref — the project code (`VENN`) or
 * the UUID — while cards only know `proto.id`, so matching on the key alone
 * misses code-keyed caches and mutations never refresh the page.
 */
function holdsPrototype(prototypeId: string) {
  return (query: Query) => {
    const key = query.queryKey;
    if (key[0] !== 'prototype') return false;
    // UUID-keyed entries and their sub-queries (pulls, builds, …), as a
    // plain `qk.prototype(id)` prefix invalidation would match.
    if (key[1] === prototypeId) return true;
    return key.length === 2 && (query.state.data as PrototypeDto | undefined)?.id === prototypeId;
  };
}

/** Refetch every cached copy of the prototype, however the page keyed it. */
export function invalidatePrototype(queryClient: QueryClient, prototypeId: string) {
  return queryClient.invalidateQueries({ predicate: holdsPrototype(prototypeId) });
}

/** Patch every cached copy of the prototype in place (instant UI update). */
export function updatePrototypeCache(
  queryClient: QueryClient,
  prototypeId: string,
  update: (proto: PrototypeDto) => PrototypeDto,
) {
  queryClient.setQueriesData<PrototypeDto>(
    { predicate: holdsPrototype(prototypeId) },
    (proto) => (proto ? update(proto) : proto),
  );
}
